import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome'
import Icon from 'components/Icon'
import PropTypes from 'prop-types'
import { forwardRef } from 'react'
import { Button } from 'react-bootstrap'
import { klass } from 'utils'
import Tip from '../Tip'

/**
 * An extension on top of Bootstrap's Button to integrate with FontAwesomeIcon.
 * @param icon {null|string|string[]|object} Either an icon name, an array with icon type+name (e.g. [far, calendar]),
 * or an element with the actual icon; Passing an element is mostly for stacks or complex icons, and will ignore the
 * rest of icon params.
 * @param iconSize {string} Defines the icon size, if needed
 * @param iconRotate {number} Defines the icon rotation, if needed
 * @param swap {boolean} By default, it's text+icon; if this is true, it becomes icon+text
 * @param pulse {boolean} Icon: makes it pulse/spin
 * @param fixedWidth {boolean} Icon: makes it "monospace"
 * @param showIconFrom {string} Which screen size starts showing the icon - e.g. "sm" creates "d-none d-sm-inline-block"
 * @param disabled {boolean|string|JSX.Element} If it's a string, adds a tooltip together with disabling the button - same as `disabled=true tooltipTxt="the reason"`
 * @param noSlash {boolean} If given, the disabled button won't slash the icon. Should be used only on special buttons where the slash would be harmful! (e.g. ActBtn's temporary disabled states)
 * @param tooltipTxt {string} The text that will be shown at the tooltip. Inserts a (?) at the opposite side of the icon.
 * @param attr Everything else goes into the Button component
 * @param children
 * @returns {Button}
 */
const Btn = forwardRef(({ tooltipTxt, icon, iconRotate, iconSize, swap, pulse, fixedWidth, showIconFrom, children, disabled = false, noSlash = false, ...attr }, ref) => {
	let iconClass = 'text-bold-reset'
	if (showIconFrom) {
		iconClass += ` d-none d-${showIconFrom}-inline-block`
	}

	if (typeof disabled == 'string' || typeof disabled == 'object') {
		tooltipTxt = disabled
		disabled   = true
	}

	let tooltipIndicator = tooltipTxt? <FA icon="question-circle" size="sm"/> : null
	let content = null
	if (children) {
		content = (typeof children != 'object' || children.type != 'span')? <span>{children}</span> : children
	} else {
		iconClass += ' mr-0'
	}

	if (typeof icon == 'string' && icon.includes('slash')) {
		noSlash = true
	}
	let IconBase = disabled && !noSlash? Icon.Slashed : FA
	let IconEl   = (typeof icon == 'string' || Array.isArray(icon))?
		<IconBase {...{ icon, pulse, fixedWidth }} rotation={iconRotate} size={iconSize} className={iconClass}/> :
		icon

	const button = <Button {...attr} {...{ ref, disabled }}>
		{swap? <>{IconEl} {content} {tooltipIndicator}</> : <>{tooltipIndicator} {content} {IconEl}</>}
	</Button>

	//we need the span wrapper so the tooltip has something interactive inside to trigger it, if the button is disabled
	return !tooltipTxt? button : <Tip text={tooltipTxt}><span {...klass('btn-container', { disabled })}>{button}</span></Tip>
})

//don't forget to include new props in Btn.d.ts too
Btn.propTypes = {
	icon        : PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
	iconSize    : PropTypes.oneOf(['xs', 'lg', 'sm', '1x', '2x', '3x', '4x', '5x', '6x', '7x', '8x', '9x', '10x']),
	iconRotate  : PropTypes.oneOf([90, 180, 270]),
	swap        : PropTypes.bool,
	pulse       : PropTypes.bool,
	fixedWidth  : PropTypes.bool,
	showIconFrom: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
	disabled    : PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.node]),
	noSlash     : PropTypes.bool,
	tooltipTxt  : PropTypes.node,
}

export default Btn
